import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableContainer,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  Card,
  CardContent,
  Button,
  Alert,
} from "@mui/material";
import { Add as AddIcon, Casino as CasinoIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { RootState, GameData, GamelistModalDetails } from "types";
import MUICashTableHeader from "../sections/Tables/Headers/CashTableHeader";
import MUITournamentTableHeader from "../sections/Tables/Headers/TourneyTableHeader";
import CashGameTableBody from "../sections/Tables/Bodys/CashGameTableBody";
import TournamentTableBody from "../sections/Tables/Bodys/TournamentTableBody";
import PrivateGameSearch from "../PrivateGameSearch";
import useUnifiedGameList from "app/Hooks/useUnifiedGameList";
import {
  selectPublicCashGamesCompat,
  selectPublicTournamentGamesCompat,
  selectPrivateCashGamesCompat,
  selectPrivateTournamentGamesCompat,
  selectGamesLoading,
} from "store/selectors/gamesSelectors";

interface UnifiedGameListProps {
  setModalDets: (dets: GamelistModalDetails) => void;
  setGameData: (data: GameData) => void;
}

type GameType = "all" | "cash" | "tournament";
type AccessType = "all" | "public" | "private";

const UnifiedGameList: React.FC<UnifiedGameListProps> = ({
  setModalDets,
  setGameData,
}) => {
  const navigate = useNavigate();
  const [gameTypeFilter, setGameTypeFilter] = useState<GameType>("all");
  const [accessFilter, setAccessFilter] = useState<AccessType>("all");

  // Use the unified games API
  useUnifiedGameList();

  // Get all game lists from unified selectors (with backward compatibility)
  const publicCashList = useSelector(selectPublicCashGamesCompat);
  const publicTourneyList = useSelector(selectPublicTournamentGamesCompat);
  const privateCashList = useSelector(selectPrivateCashGamesCompat);
  const privateTourneyList = useSelector(selectPrivateTournamentGamesCompat);
  const isLoading = useSelector(selectGamesLoading);
  const userToken = useSelector((state: RootState) => state.user.token);

  const isHost = userToken?.isHost;

  // Combine and filter games
  const allGames = useMemo(() => {
    const games: Array<GameData & { access: string; type: string }> = [];

    // Add public cash games
    if (publicCashList) {
      Object.values(publicCashList).forEach((game) => {
        games.push({ ...game, access: "public", type: "cash" } as GameData & { access: string; type: string });
      });
    }

    // Add public tournament games
    if (publicTourneyList) {
      Object.values(publicTourneyList).forEach((game) => {
        games.push({ ...game, access: "public", type: "tournament" } as GameData & { access: string; type: string });
      });
    }

    // Add private cash games
    if (privateCashList) {
      Object.values(privateCashList).forEach((game) => {
        games.push({ ...game, access: "private", type: "cash" } as GameData & { access: string; type: string });
      });
    }

    // Add private tournament games
    if (privateTourneyList) {
      Object.values(privateTourneyList).forEach((game) => {
        games.push({ ...game, access: "private", type: "tournament" } as GameData & { access: string; type: string });
      });
    }

    return games;
  }, [publicCashList, publicTourneyList, privateCashList, privateTourneyList]);

  // Filter games based on selected filters
  const filteredGames = useMemo(() => {
    return allGames.filter((game) => {
      const typeMatch = gameTypeFilter === "all" || game.type === gameTypeFilter;
      const accessMatch = accessFilter === "all" || game.access === accessFilter;
      return typeMatch && accessMatch;
    });
  }, [allGames, gameTypeFilter, accessFilter]);

  // Group filtered games by type for rendering
  const cashGames = filteredGames.filter((game) => game.type === "cash");
  const tournamentGames = filteredGames.filter((game) => game.type === "tournament");

  const handleGameTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newGameType: GameType,
  ) => {
    if (newGameType !== null) {
      setGameTypeFilter(newGameType);
    }
  };

  const handleAccessChange = (event: any) => {
    setAccessFilter(event.target.value as AccessType);
  };

  const renderNoGamesMessage = () => (
    <Card sx={{ mt: 4, textAlign: "center" }}>
      <CardContent sx={{ py: 6 }}>
        <CasinoIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          No Games Found
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {gameTypeFilter === "all" && accessFilter === "all"
            ? "There are currently no active games available."
            : `No ${gameTypeFilter === "all" ? "" : gameTypeFilter} ${accessFilter === "all" ? "" : accessFilter} games found.`}
        </Typography>
        
        {isHost && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              As a host, you can create a new game to get started!
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => navigate("/hostpanel/create")}
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1,
              }}
            >
              Create New Game
            </Button>
          </Box>
        )}
        
        {!isHost && (
          <Alert severity="info" sx={{ mt: 2, maxWidth: 500, mx: "auto" }}>
            <Typography variant="body2">
              Want to host your own games? 
              <Button 
                size="small" 
                onClick={() => navigate("/settings/host-registration")}
                sx={{ ml: 1 }}
              >
                Become a Host
              </Button>
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  const renderGameTable = (games: Array<GameData & { access: string; type: string }>, type: "cash" | "tournament") => {
    if (games.length === 0) return null;

    // Convert to the format expected by the table components
    const gameData: Record<string, GameData> = {};
    games.forEach((game, index) => {
      gameData[game.id || `game-${index}`] = game;
    });

    return (
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textAlign: "center",
            mb: 2,
          }}
        >
          {type === "cash" ? "Cash Games" : "Tournament Games"}
        </Typography>
        <TableContainer
          component={Paper}
          elevation={10}
          sx={{ borderRadius: "20px" }}
        >
          <Table className="tablelist" aria-label={`${type} games table`}>
            {type === "cash" ? (
              <MUICashTableHeader />
            ) : (
              <MUITournamentTableHeader />
            )}
            {type === "cash" ? (
              <CashGameTableBody
                data={gameData}
                setModalDets={setModalDets}
                setGameData={setGameData}
              />
            ) : (
              <TournamentTableBody
                data={gameData}
                setModalDets={setModalDets}
                setGameData={setGameData}
              />
            )}
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, marginBottom: "50px" }}>
      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }} elevation={2}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: "center" }}>
          Filter Games
        </Typography>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={3}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="center"
        >
          <ToggleButtonGroup
            value={gameTypeFilter}
            exclusive
            onChange={handleGameTypeChange}
            aria-label="game type filter"
            size="small"
          >
            <ToggleButton value="all" aria-label="all games">
              All Games
            </ToggleButton>
            <ToggleButton value="cash" aria-label="cash games">
              Cash Games
            </ToggleButton>
            <ToggleButton value="tournament" aria-label="tournament games">
              Tournaments
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Access Type</InputLabel>
            <Select
              value={accessFilter}
              onChange={handleAccessChange}
              label="Access Type"
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="public">Public</MenuItem>
              <MenuItem value="private">Private</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Games Display */}
      {filteredGames.length === 0 ? (
        renderNoGamesMessage()
      ) : (
        <Box>
          {(gameTypeFilter === "all" || gameTypeFilter === "cash") &&
            renderGameTable(cashGames, "cash")}
          {(gameTypeFilter === "all" || gameTypeFilter === "tournament") &&
            renderGameTable(tournamentGames, "tournament")}
        </Box>
      )}

      {/* Private Game Search */}
      {(accessFilter === "all" || accessFilter === "private") && (
        <Box sx={{ mt: 4 }}>
          <PrivateGameSearch />
        </Box>
      )}
    </Box>
  );
};

export default UnifiedGameList;